import { useContact } from '@/context/Contact';
import { usePageModels } from '@/context/PageModels';
import { PrivacyConsentModalContentProps } from '@/features/shared/portal/PrivacyConsentModal/PrivacyConsentModal.interfaces';
import { updateContact } from '@/services';
import { isPortalPathname } from '@/utils/portal';
import { getOr } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { PrivacyConsentValues } from '../PortalPrivacyConsent.interface';

const COOKIE_MODAL_LABEL_KEY = 'cookieModal';

enum CookieModalRbKeys {
  TITLE = 'modal_title_label',
  CONTENT = 'modal_rich_content_label',
  ACCEPT_LABEL = 'modal_accept_button_label',
  DECLINE_LABEL = 'modal_decline_button_label',
}

export const usePortalPrivacyConsentModal = () => {
  const [localShowModal, setLocalShowModal] = useState(true);
  const { contactInfo, registrationStatus } = useContact();
  const { pathname } = useLocation();
  const { labels } = usePageModels();
  const { requireContactInfo } = useContact();

  const updatePrivacyConsent = useCallback(
    async (newPrivacyConsentValue: PrivacyConsentValues) => {
      try {
        await updateContact({
          values: {
            gen_portalprivacyconsent: newPrivacyConsentValue,
          },
        });

        requireContactInfo();
      } catch (e) {
        console.error('An error occurred trying to update the privacy consent flag', e);
      }
    },
    [requireContactInfo],
  );

  const handleAccept = useCallback(() => {
    setLocalShowModal(false);
    return updatePrivacyConsent(PrivacyConsentValues.CONSENT_ACCEPTED).then();
  }, [setLocalShowModal, updatePrivacyConsent]);

  const handleDecline = useCallback(() => {
    setLocalShowModal(false);
    return updatePrivacyConsent(PrivacyConsentValues.CONSENT_REJECTED).then();
  }, [setLocalShowModal, updatePrivacyConsent]);

  const consentNeeded = useMemo(
    () =>
      contactInfo?.portalPrivacyConsent === PrivacyConsentValues.CONSENT_NEEDED ||
      contactInfo?.portalPrivacyConsent === null,
    [contactInfo?.portalPrivacyConsent],
  );

  const availableForDisplay = useMemo(
    () =>
      isPortalPathname(pathname) &&
      registrationStatus.isApproved &&
      consentNeeded &&
      localShowModal,
    [pathname, consentNeeded, localShowModal, registrationStatus],
  );

  const modalProps = useMemo<PrivacyConsentModalContentProps>(() => {
    const cookieModalLabels = getOr({}, COOKIE_MODAL_LABEL_KEY, labels);
    const { TITLE, CONTENT, ACCEPT_LABEL, DECLINE_LABEL } = CookieModalRbKeys;
    return {
      title: getOr('', TITLE, cookieModalLabels),
      content: getOr('', CONTENT, cookieModalLabels),
      labels: {
        accept: getOr('', ACCEPT_LABEL, cookieModalLabels),
        decline: getOr('', DECLINE_LABEL, cookieModalLabels),
      },
      onAcceptClick: handleAccept,
      onDeclineClick: handleDecline,
    };
  }, [labels, handleAccept, handleDecline]);

  return {
    availableForDisplay,
    modalProps,
  };
};
